import { getBackgroundCheckConfigForType } from '@/backend/features/background-check/utils/getBackgroundCheckConfigForType';
import { ExtendedAccountData } from '@/backend/features/contact-management/entities/extended-account/types';
import { AppContext } from '@juvare/platform-app/App';
import { AppState } from '@juvare/platform-sdk-react/types';
import { createBackgroundCheckHistory } from '../../../background-tasks';
import { TypedBackgroundCheckColumns } from '../../../shared/entities/background-check/types';
import { BackgroundCheckStatus, BackgroundCheckType } from '../../../types';
import { isBackgroundCheckConsentGiven, isBackgroundCheckFormComplete } from '../../../utils';

export function recalculateBackgroundCheckStatus(
    context: AppContext<AppState>,
    {
        backgroundCheck,
        accountData,
        skipHistoryCreation,
    }: {
        backgroundCheck: TypedBackgroundCheckColumns;
        accountData: ExtendedAccountData;
        skipHistoryCreation: boolean;
    },
): { backgroundCheck: TypedBackgroundCheckColumns; createHistory?: () => void } {
    const backgroundCheckType: BackgroundCheckType | undefined = backgroundCheck.type;
    let backgroundCheckStatus: BackgroundCheckStatus = 'readyToSubmit';

    if (!backgroundCheckType) {
        return { backgroundCheck };
    }

    const backgroundCheckConfigForType = getBackgroundCheckConfigForType(context, backgroundCheckType);

    if (!backgroundCheckConfigForType?.enabled) {
        return { backgroundCheck };
    }

    const shouldCheckForm = backgroundCheckConfigForType?.requiresFormValidation;
    const shouldCheckConsent = backgroundCheckConfigForType?.requiresConsent;

    if (!isBackgroundCheckConsentGiven(backgroundCheck) && shouldCheckConsent) {
        backgroundCheckStatus = 'didNotConsent';
    } else if (!isBackgroundCheckFormComplete(accountData, backgroundCheckType)) {
        backgroundCheckStatus = shouldCheckForm ? 'formIncomplete' : 'readyToSubmit';
    }

    const createHistory = () => {
        if (backgroundCheck.status !== backgroundCheckStatus) {
            createBackgroundCheckHistory(context, {
                requestedBy: backgroundCheck.requestedBy ?? 'System',
                backgroundCheckStatus: backgroundCheck.status,
                nextStatus: backgroundCheckStatus,
                accountId: backgroundCheck.accountId,
                type: backgroundCheckType,
                internalStatusChange: true,
            });
        }
    };

    if (!skipHistoryCreation) {
        createHistory();
    }

    return {
        backgroundCheck: {
            ...backgroundCheck,
            status: backgroundCheckStatus,
            updatedAt: Date.now(),
        },
        createHistory,
    };
}
